import Dexie, { UpdateSpec } from 'dexie';
import { HustroDb, HustroDbTablesUnion } from '.';
import { logLocalRowGoneBeforeRemoteId, logServerCopyOfLocalRowDropped } from 'serviceWorker/helpers/dataLog';
import { isNonEmptyArray } from 'shared/utils/array';
import { LogLevel } from 'shared/types/logger';
import { getErrorMsg, logWithRethrow, Operation } from './helpers';
import { wrapQuery } from './queryWrapper';

/** A row created on this device: the server id comes once the server stored it. */
interface LocallyCreatedRow {
  localId: number;
  _id?: string;
}

export type AssignRemoteId<TRow extends LocallyCreatedRow> = (
  localId: number,
  remoteId: string,
  changes?: Partial<TRow>
) => Promise<number>;

/**
 * Gives a row created on this device the id the server assigned to it, with
 * the `changes` that come with it.
 *
 * A sync may have stored the server's copy of the row first: syncs match rows
 * by `_id`, which this row did not have yet, so the copy became a second row
 * and the unique `_id` index refused the id (PT-7278). The copy is dropped in
 * the same transaction. The local row stays: the upload and the page refer to
 * its `localId`, and the next sync refreshes its server fields.
 *
 * When the local row is gone (a flush or a removal during the upload),
 * nothing changes: the server's copy, if any, stays the one row of the id.
 */
export function assignRemoteIdFactory<TRow extends LocallyCreatedRow>(
  db: HustroDb,
  tableName: HustroDbTablesUnion,
  table: Dexie.Table<TRow>
): AssignRemoteId<TRow> {
  return wrapQuery(db, async (localId, remoteId, changes = {}) => {
    const outcome = await db
      .transaction('rw', table, async function assignRemoteIdTransaction() {
        if (!(await table.get(localId))) {
          return { isLocalRowGone: true, droppedLocalIds: [] };
        }
        const rowsWithTheId = await table.where('_id').equals(remoteId).primaryKeys();
        const serverCopies = rowsWithTheId.filter(
          (rowLocalId): rowLocalId is number => typeof rowLocalId === 'number' && rowLocalId !== localId
        );
        await table.bulkDelete(serverCopies);
        await table.update(localId, { ...changes, _id: remoteId } as UpdateSpec<TRow>);
        return { isLocalRowGone: false, droppedLocalIds: serverCopies };
      })
      .catch((error: unknown) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, tableName),
          errorObj: error,
          additionalInfo: { query: { localId, remoteId } },
        })
      );
    if (outcome.isLocalRowGone) {
      logLocalRowGoneBeforeRemoteId(tableName, { localId, remoteId });
    } else if (isNonEmptyArray(outcome.droppedLocalIds)) {
      logServerCopyOfLocalRowDropped(tableName, { localId, remoteId, droppedLocalIds: outcome.droppedLocalIds });
    }
    return localId;
  });
}
